import React, { FC, useEffect, useState } from "react"
import { ArtistTrendEconomyData, ArtistTrendEconomy, RoyaltyChartData } from "../../../../types"
import { KButton, KSpan, KTitleSpan, KDropdown, KTooltip } from "kahuna-base-react-components"
import NotFound from "../../../NotFound"
import { lang } from "../../../../constants/languages"
import { formatNumber } from "../../../../utility"
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Filler
} from "chart.js"
import { Line } from "react-chartjs-2"
import { ARTIST_TREND_ECONOMY_ROYALTY_TYPE, chartLabels } from "../../../../constants"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"

ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend, Filler)

type StreamsTrackMonthsProps = {
  artistTrendEconomyData: ArtistTrendEconomyData
  setOffset: (offset: number) => void
  offset: number
  limit: number
  selectedRoyaltyType?: KSelectOption
  setSelectedRoyaltyType: (option?: KSelectOption) => void
  selectedResults: ArtistTrendEconomy[]
  setSelectedResults: (results: ArtistTrendEconomy[]) => void
  hasNext: boolean
}

const StreamsTrackMonths: FC<StreamsTrackMonthsProps> = (props) => {
  const { artistTrendEconomyData, offset, setOffset, limit, selectedResults, setSelectedResults, hasNext } = props

  const [hoverId, setHoverId] = useState<number>()
  const [selectedPlatforms, setSelectedPlatforms] = useState<KSelectOption[]>([])
  const [selectedCountries, setSelectedCountries] = useState<KSelectOption[]>([])

  const royaltyTypeOptions: KSelectOption[] = [
    { label: "Advance Deals", value: 1, value2: ARTIST_TREND_ECONOMY_ROYALTY_TYPE.ADVANCE },
    { label: "Distribution Deals", value: 2, value2: ARTIST_TREND_ECONOMY_ROYALTY_TYPE.DISTRIBUTION }
  ]

  const platformOptions: KSelectOption[] = Object.entries(chartLabels.platform).map(([key, value], index) => {
    return { label: value, value: index, value2: key }
  })

  const countryOptions: KSelectOption[] = Object.entries(chartLabels.storeFront).map(([key, value], index) => {
    return { label: value, value: index, value2: key }
  })

  const lineChartData = {
    labels: artistTrendEconomyData.chartData.map((item) => item.monthLabel),
    datasets: [
      {
        label: "Spotify Streams",
        data: artistTrendEconomyData.chartData.map((item) => item.streams),
        borderColor: "#56E099",
        backgroundColor: "rgba(86, 224, 153, 0.1)",
        fill: true,
        tension: 0.4,
        pointRadius: 3,
        pointHoverRadius: 6,
        pointBackgroundColor: "#56E099"
      }
    ]
  }

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        mode: "index" as const,
        intersect: false,
        callbacks: {
          label: (context: any) => `Spotify Streams: ${formatNumber(context.raw)}`
        }
      },
      datalabels: { display: false }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: "#666" }
      },
      y: {
        grid: { color: "#f0f0f0" },
        ticks: {
          color: "#666",
          callback: (value: any) => formatNumber(value)
        },
        beginAtZero: true
      }
    }
  }

  // @ts-ignore
  const sumBy = (list: ArtistTrendEconomy[] = [], key: string) => list.reduce((sum, item) => sum + (item[key] || 0), 0)

  const renderRoyaltiesDetail = (artistTrendEconomy: ArtistTrendEconomy, marginTop: number) => {
    return (
      <div style={{ width: "35%", marginTop }}>
        <div className="flex">
          <div className="flex flex-col" style={{ width: "35%" }}>
            <KSpan text={`${formatNumber((artistTrendEconomy.streams30NewRelease || 0).toFixed(1))}`} />
          </div>
          <div className="flex flex-col" style={{ width: "35%" }}>
            <KSpan text={`${formatNumber((artistTrendEconomy.streams30BackCatalog || 0).toFixed(1))}`} />
          </div>
          <div className="flex flex-col" style={{ width: "30%" }}>
            <KSpan text={`${formatNumber((artistTrendEconomy.streams30Takedown || 0).toFixed(1))}`} />
          </div>
        </div>
        <div className="flex mt-2">
          <div className="flex flex-col" style={{ width: "35%" }}>
            <KSpan text={`${formatNumber((artistTrendEconomy.streams60NewRelease || 0).toFixed(1))}`} />
          </div>
          <div className="flex flex-col" style={{ width: "35%" }}>
            <KSpan text={`${formatNumber((artistTrendEconomy.streams60BackCatalog || 0).toFixed(1))}`} />
          </div>
          <div className="flex flex-col" style={{ width: "30%" }}>
            <KSpan text={`${formatNumber((artistTrendEconomy.streams60Takedown || 0).toFixed(1))}`} />
          </div>
        </div>
        <div className="flex mt-2">
          <div className="flex flex-col" style={{ width: "35%" }}>
            <KSpan text={`${formatNumber((artistTrendEconomy.streams90NewRelease || 0).toFixed(1))}`} />
          </div>
          <div className="flex flex-col" style={{ width: "35%" }}>
            <KSpan text={`${formatNumber((artistTrendEconomy.streams90BackCatalog || 0).toFixed(1))}`} />
          </div>
          <div className="flex flex-col" style={{ width: "30%" }}>
            <KSpan text={`${formatNumber((artistTrendEconomy.streams90Takedown || 0).toFixed(1))}`} />
          </div>
        </div>
      </div>
    )
  }

  const renderPlatforms = (platform?: string | string[]) => {
    if (!platform) {
      return <div />
    }

    const platformsArray = Array.isArray(platform) ? platform : [platform]

    const mergedPlatform = platformsArray.reduce<Record<string, number>>((acc, platformStr) => {
      if (!platformStr) return acc

      let parsed: Record<string, number> = {}

      try {
        parsed = JSON.parse(platformStr)
      } catch (e) {
        console.error("Invalid platform JSON", e)
        return acc
      }

      Object.entries(parsed).forEach(([key, value]) => {
        acc[key] = (acc[key] || 0) + Number(value)
      })

      return acc
    }, {})

    const selectedPlatformKeys = selectedPlatforms.map((p) => p.value2)
    const sumPlatforms = Object.values(mergedPlatform).reduce((a, b) => a + b, 0) || 1

    return (
      <div>
        {Object.entries(mergedPlatform)
          .filter(([key, value]) => selectedPlatformKeys.length === 0 || selectedPlatformKeys.includes(key))
          .map(([key, value]) => (
            <div key={key} className="flex gap-1">
              <KSpan fontWeight={600} color="#111" text={`${key}: `} />
              <KSpan text={`${((value / sumPlatforms) * 100).toFixed(1)}%`} />
            </div>
          ))}
      </div>
    )
  }

  const renderCountries = (paddingTop: number, country?: string | string[]) => {
    if (!country) {
      return <div />
    }

    const countryArray = Array.isArray(country) ? country : [country]

    const mergedCountries = countryArray.reduce<
      Record<string, { streams_30: number; streams_60: number; streams_90: number }>
    >((acc, countryStr) => {
      if (!countryStr) return acc

      let parsed: Record<string, any> = {}

      try {
        parsed = JSON.parse(countryStr)
      } catch (e) {
        console.error("Invalid country JSON", e)
        return acc
      }

      Object.entries(parsed).forEach(([countryCode, values]: any) => {
        if (!acc[countryCode]) {
          acc[countryCode] = {
            streams_30: 0,
            streams_60: 0,
            streams_90: 0
          }
        }

        acc[countryCode].streams_30 += Number(values.streams_30 || 0)
        acc[countryCode].streams_60 += Number(values.streams_60 || 0)
        acc[countryCode].streams_90 += Number(values.streams_90 || 0)
      })

      return acc
    }, {})

    const selectedCountryKeys = selectedCountries.map((p) => p.value2)
    const sumCountries90 = Object.values(mergedCountries).reduce((a, b) => a + b.streams_90, 0) || 1
    const sumCountries60 = Object.values(mergedCountries).reduce((a, b) => a + b.streams_60, 0) || 1
    const sumCountries30 = Object.values(mergedCountries).reduce((a, b) => a + b.streams_30, 0) || 1

    return (
      <div style={{ width: "42%", paddingTop }}>
        {Object.entries(mergedCountries)
          .filter(
            ([countryCode, values]) => selectedCountryKeys.length === 0 || selectedCountryKeys.includes(countryCode)
          )
          .map(([countryCode, values]) => (
            <div key={countryCode} className="flex" style={{ height: 19.7 }}>
              {/* Country */}
              <div style={{ width: "13%" }}>
                <KSpan fontWeight={600} color="#111" text={`${countryCode}:`} />
              </div>

              {/* 90 */}
              <div style={{ width: "30%" }}>
                <KSpan text={`${((values.streams_90 / sumCountries90) * 100).toFixed(1)}%`} />
              </div>

              {/* 60 */}
              <div style={{ width: "30%" }}>
                <KSpan text={`${((values.streams_60 / sumCountries60) * 100).toFixed(1)}%`} />
              </div>

              {/* 30 */}
              <div style={{ width: "27%" }}>
                <KSpan text={`${((values.streams_30 / sumCountries30) * 100).toFixed(1)}%`} />
              </div>
            </div>
          ))}
      </div>
    )
  }

  const DealColumn = ({ results, streamKey }: { results: ArtistTrendEconomy[]; streamKey: string }) => {
    const streams = sumBy(results, streamKey)

    return (
      <div style={{ width: "12%" }}>
        <KSpan fontWeight={600} color="#111" text={formatNumber(streams)} />
      </div>
    )
  }

  const onChangeRoyaltyType = (selected: KSelectOption | undefined) => {
    setOffset(0)
    setSelectedResults([])
    props.setSelectedRoyaltyType(selected as KSelectOption)
  }

  const DealRow = ({ title, royaltyType }: { title: string; royaltyType?: string }) => {
    const periods = [{ stream: "streams90" }, { stream: "streams60" }, { stream: "streams30" }]

    let results: ArtistTrendEconomy[] = artistTrendEconomyData.totals
    if (royaltyType === ARTIST_TREND_ECONOMY_ROYALTY_TYPE.ADVANCE) {
      results = artistTrendEconomyData.totals.filter((t) => t.royaltyType === ARTIST_TREND_ECONOMY_ROYALTY_TYPE.ADVANCE)
    } else if (royaltyType === ARTIST_TREND_ECONOMY_ROYALTY_TYPE.DISTRIBUTION) {
      results = artistTrendEconomyData.totals.filter(
        (t) => t.royaltyType === ARTIST_TREND_ECONOMY_ROYALTY_TYPE.DISTRIBUTION
      )
    }

    const mergedResult: ArtistTrendEconomy | undefined = results.reduce((acc, curr) => {
      if (!acc) {
        return { ...curr }
      }

      return {
        ...acc,
        // repeat for new_release / back_catalog / takedown if present
        streams30NewRelease: acc.streams30NewRelease + curr.streams30NewRelease,
        streams60NewRelease: acc.streams60NewRelease + curr.streams60NewRelease,
        streams90NewRelease: acc.streams90NewRelease + curr.streams90NewRelease,
        streams30BackCatalog: acc.streams30BackCatalog + curr.streams30BackCatalog,
        streams60BackCatalog: acc.streams60BackCatalog + curr.streams60BackCatalog,
        streams90BackCatalog: acc.streams90BackCatalog + curr.streams90BackCatalog,
        streams30Takedown: acc.streams30Takedown + curr.streams30Takedown,
        streams60Takedown: acc.streams60Takedown + curr.streams60Takedown,
        streams90Takedown: acc.streams90Takedown + curr.streams90Takedown
      }
    }, undefined as ArtistTrendEconomy | undefined)

    return (
      <div className="p-2 mb-3" style={{ width: "100%", background: "#00000008", borderRadius: "5px" }}>
        <div className="flex" style={{ width: "100%" }}>
          <div style={{ width: "26%" }}>
            <div className="p-1">
              <KSpan fontWeight={600} color="#111" text={title} />
            </div>
          </div>
          <div style={{ width: "3%" }} className="flex flex-col" />

          {periods.map((p) => (
            <DealColumn key={p.stream} results={results} streamKey={p.stream} />
          ))}

          <div className="flex flex-col" style={{ width: "12%" }}>
            <KSpan color="#111" fontWeight={600} text={`New Release`} />
            <KSpan color="#111" fontWeight={600} text={`30 / 60 / 90`} />
          </div>
          <div className="flex flex-col" style={{ width: "12%" }}>
            <KSpan color="#111" fontWeight={600} text={`Back Catalog`} />
            <KSpan color="#111" fontWeight={600} text={`30 / 60 / 90`} />
          </div>
          <div className="flex flex-col" style={{ width: "10%" }}>
            <KSpan color="#111" fontWeight={600} text={`Takedown`} />
            <KSpan color="#111" fontWeight={600} text={`30 / 60 / 90`} />
          </div>
        </div>
        <div className="flex" style={{ width: "100%" }}>
          <div style={{ width: "23%" }}>
            <div className="p-1 pt-3">{renderPlatforms(results.map((r) => r.streamsPlatform || ""))}</div>
          </div>
          {renderCountries(
            10,
            results.map((r) => r.country || "")
          )}
          {mergedResult && renderRoyaltiesDetail(mergedResult, 14)}
        </div>
      </div>
    )
  }

  return (
    <div className="w-full h-full flex flex-col gap-4">
      {/* Chart Section */}
      <div
        className="py-5 px-7"
        style={{
          borderRadius: "10px",
          border: "1px solid var(--stroke-soft-200, #F3F3F3)",
          background: "var(--bg-white-0, #FFF)",
          boxShadow: "0px 2px 4px 0px rgba(27, 28, 29, 0.04)"
        }}
      >
        <div className="flex items-center gap-4 mb-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#56E099" }}></div>
            <KSpan text="Streams Spotify" fontWeight={500} />
          </div>
          <KSpan text={formatNumber(artistTrendEconomyData.totalStreams)} fontWeight={600} fontSize={18} />
        </div>
        <div style={{ height: 200 }}>
          {artistTrendEconomyData.chartData.length > 0 ? (
            <Line data={lineChartData} options={lineChartOptions} />
          ) : (
            <div className="flex items-center justify-center h-full">
              <KSpan text="No chart data available" color="#999" />
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-2">
        <KDropdown
          isClearable
          width={160}
          menuWidth={170}
          placeholder="Deal Type"
          options={royaltyTypeOptions}
          selected={props.selectedRoyaltyType}
          onSelect={(selected) => onChangeRoyaltyType(selected as KSelectOption)}
        />
        <KDropdown
          allowContainerShrink
          isClearable
          isMulti
          menuWidth={150}
          closeMenuOnSelect={false}
          placeholder="Filter Platforms"
          options={platformOptions}
          selected={selectedPlatforms}
          onBlur={(selected) => setSelectedPlatforms(selected as KSelectOption[])}
          onSelect={() => {}}
        />
        <KDropdown
          allowContainerShrink
          isClearable
          isMulti
          menuWidth={150}
          closeMenuOnSelect={false}
          placeholder="Filter Countries"
          options={countryOptions}
          selected={selectedCountries}
          onBlur={(selected) => setSelectedCountries(selected as KSelectOption[])}
          onSelect={() => {}}
        />
      </div>
      {/* Table Section */}
      <div
        className="py-5 px-7"
        style={{
          borderRadius: "10px",
          border: "1px solid var(--stroke-soft-200, #F3F3F3)",
          background: "var(--bg-white-0, #FFF)",
          boxShadow: "0px 2px 4px 0px rgba(27, 28, 29, 0.04)"
        }}
      >
        <div>
          <div className="flex mb-4" style={{ width: "100%" }}>
            <div style={{ width: "26%" }}>
              <KSpan color="#111" text={"User"} />
            </div>
            <div style={{ width: "13%" }}>
              <KSpan color="#111" text={"60-90 days"} />
            </div>
            <div style={{ width: "13%" }}>
              <KSpan color="#111" text={"30-60 days"} />
            </div>
            <div className="flex gap-1 items-center" style={{ width: "13%" }}>
              <KSpan color="#111" text={"0-30 days"} />
              {artistTrendEconomyData.missingDays && artistTrendEconomyData.missingDays.length > 0 && (
                <KTooltip
                  backgroundColor="#000"
                  padding="8px 12px"
                  arrowColor="#000"
                  width="160px"
                  position="top"
                  content={
                    <div className="flex flex-col gap-1">
                      <KSpan text="Missing data for:" color="#FFF" fontSize={12} fontWeight={600} />
                      {artistTrendEconomyData.missingDays.slice(0, 10).map((day) => (
                        <KSpan key={day} text={day} color="#FFF" fontSize={11} />
                      ))}
                      {artistTrendEconomyData.missingDays.length > 10 && (
                        <KSpan
                          text={`...and ${artistTrendEconomyData.missingDays.length - 10} more days`}
                          color="#999"
                          fontSize={11}
                        />
                      )}
                    </div>
                  }
                >
                  <div className="cursor-help flex items-center">
                    <img src="/account_icons/info-circle.svg" alt="info" style={{ width: "16px", height: "16px" }} />
                  </div>
                </KTooltip>
              )}
            </div>
            <div style={{ width: "12%" }}>
              <KSpan color="#111" text={"New Release"} />
            </div>
            <div style={{ width: "12%" }}>
              <KSpan color="#111" text={"Back Catalog"} />
            </div>
            <div style={{ width: "11%" }}>
              <KSpan color="#111" text={"Takedown"} />
            </div>
          </div>
          {selectedResults && !(selectedResults.length > 0) && (
            <div className="flex p-5 w-full justify-center mt-[100px]">
              <NotFound title={lang.not_found.trends.title} description={lang.not_found.trends.desc} imageWidth={400} />
            </div>
          )}
          <DealRow title="TOTAL" royaltyType={undefined} />
          <DealRow title="DISTRIBUTION DEALS" royaltyType={ARTIST_TREND_ECONOMY_ROYALTY_TYPE.DISTRIBUTION} />
          <DealRow title="ADVANCE DEALS" royaltyType={ARTIST_TREND_ECONOMY_ROYALTY_TYPE.ADVANCE} />

          {selectedResults?.map((artistTrendEconomy) => {
            const background = hoverId === artistTrendEconomy.userId ? "#00000008" : "transparent"
            return (
              <div
                key={`trend_top_track_${artistTrendEconomy.userId}_${artistTrendEconomy.royaltyType}_${Math.random()}`}
                onMouseOver={() => setHoverId(artistTrendEconomy.userId)}
              >
                <div className="flex p-2" style={{ width: "100%", background, borderRadius: "5px" }}>
                  <div style={{ width: "26%" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px"
                      }}
                    >
                      <img
                        src={artistTrendEconomy?.user?.photo || "/account_icons/avatar.svg"}
                        alt={artistTrendEconomy?.user?.firstName}
                        style={{ width: "50px", height: "50px", borderRadius: "50%" }}
                      />
                      <div>
                        <div>
                          <KTitleSpan
                            fontSize={16}
                            lineHeight="20px"
                            text={`${artistTrendEconomy?.user?.firstName} ${artistTrendEconomy?.user?.lastName}`}
                          />
                        </div>
                        <div>
                          <KSpan fontSize={12} text={artistTrendEconomy?.user?.email || "-"} />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div style={{ width: "2.2%" }} className="flex flex-col" />
                  <div style={{ width: "12.8%" }} className="flex flex-col gap-1">
                    <KSpan text="60-90" color="#111" fontWeight={600} />
                    <KSpan fontSize={15} text={`${formatNumber((artistTrendEconomy.streams90 || 0).toFixed(1))}`} />
                  </div>
                  <div style={{ width: "12.5%" }} className="flex flex-col gap-1">
                    <KSpan text="30-60" color="#111" fontWeight={600} />
                    <KSpan fontSize={15} text={`${formatNumber((artistTrendEconomy.streams60 || 0).toFixed(1))}`} />
                  </div>
                  <div style={{ width: "11.5%" }} className="flex flex-col gap-1">
                    <KSpan text="0-30" color="#111" fontWeight={600} />
                    <KSpan fontSize={15} text={`${formatNumber((artistTrendEconomy.streams30 || 0).toFixed(1))}`} />
                  </div>
                  <div className="flex flex-col" style={{ width: "12%" }}>
                    <KSpan color="#111" fontWeight={600} text={`New Release`} />
                    <KSpan color="#111" fontWeight={600} text={`30 / 60 / 90`} />
                  </div>
                  <div className="flex flex-col" style={{ width: "12%" }}>
                    <KSpan color="#111" fontWeight={600} text={`Back Catalog`} />
                    <KSpan color="#111" fontWeight={600} text={`30 / 60 / 90`} />
                  </div>
                  <div className="flex flex-col" style={{ width: "11%" }}>
                    <KSpan color="#111" fontWeight={600} text={`Takedown`} />
                    <KSpan color="#111" fontWeight={600} text={`30 / 60 / 90`} />
                  </div>
                </div>
                <div className="flex p-2" style={{ width: "100%", background, borderRadius: "5px" }}>
                  <div style={{ width: "23%" }}>
                    <div className="p-1 pt-1">{renderPlatforms(artistTrendEconomy.streamsPlatform)}</div>
                  </div>
                  {renderCountries(2, artistTrendEconomy.country)}
                  {renderRoyaltiesDetail(artistTrendEconomy, 0)}
                </div>
              </div>
            )
          })}
        </div>
      </div>
      <div className="pb-4">
        {hasNext && (
          <div className="flex justify-center">
            <KButton
              onClick={() => setOffset(offset + limit)}
              text="Load More"
              width="120px"
              background="transparent"
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default StreamsTrackMonths
